import React, {useState, useMemo} from "react"
import {useFavorites} from "../../hooks/useFavorites"
import {getDeviceType} from "../../utils/versionUtils"
import {DeviceTableToolbar, DeviceTypeFilter} from "../DeviceTableToolbar"
import {DeviceTableRow} from "../DeviceTableRow"
import {ShadowTabsPanel} from "../ShadowTabsPanel"
import {DeviceTableEmpty} from "../DeviceTableEmpty"
import {DeviceTableProps} from "../DeviceTableToolbar"

const DeviceTable = ({
	data,
	onRefresh,
	selectedDevices,
	onDeviceSelectionChange,
}: DeviceTableProps) => {
	const [expandedRow, setExpandedRow] = useState<string | null>(null)
	const [activeTab, setActiveTab] = useState<"classic" | "radar">("classic")
	const [deviceTypeFilter, setDeviceTypeFilter] = useState<DeviceTypeFilter>("all")
	const {favorites, addFavorites, removeFavorites} = useFavorites()

	const filteredData = useMemo(() => {
		if (deviceTypeFilter === "all") return data
		if (deviceTypeFilter === "favorites")
			return data.filter((row) => favorites.includes(row.macAddress))
		return data.filter((row) => getDeviceType(row.softwareVersion) === deviceTypeFilter)
	}, [data, deviceTypeFilter, favorites])

	const toggleExpanded = (deviceId: string) => {
		setExpandedRow(expandedRow === deviceId ? null : deviceId)
	}

	const handleDeviceSelection = (deviceId: string) => {
		if (selectedDevices.includes(deviceId)) {
			onDeviceSelectionChange(selectedDevices.filter((id) => id !== deviceId))
		} else {
			onDeviceSelectionChange([...selectedDevices, deviceId])
		}
	}

	const handleSelectAll = () => {
		if (selectedDevices.length === filteredData.length) {
			onDeviceSelectionChange([])
		} else {
			onDeviceSelectionChange(filteredData.map((row) => row.macAddress))
		}
	}

	const isAllSelected = filteredData.length > 0 && selectedDevices.length === filteredData.length

	return (
		<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
			<DeviceTableToolbar
				data={data}
				filteredData={filteredData}
				deviceTypeFilter={deviceTypeFilter}
				selectedDevices={selectedDevices}
				favorites={favorites}
				onFilterChange={setDeviceTypeFilter}
				onAddFavorites={addFavorites}
				onRemoveFavorites={removeFavorites}
			/>
			<div className="overflow-x-auto">
				<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
					<thead className="bg-gray-50 dark:bg-gray-900/50">
						<tr>
							<th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
								<input
									type="checkbox"
									checked={isAllSelected}
									onChange={handleSelectAll}
									className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700"
								/>
							</th>
							<th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
								Symbol
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
								Device
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
								Status
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
								Last Seen
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
								Data Size
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
								FW Version
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
								Radar FW Versions
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
								Upload Status
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
								Actions
							</th>
						</tr>
					</thead>
					<tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
						{filteredData.map((row) => (
							<DeviceTableRow
								key={row.macAddress}
								row={row}
								isExpanded={expandedRow === row.macAddress}
								isSelected={selectedDevices.includes(row.macAddress)}
								isFavorite={favorites.includes(row.macAddress)}
								activeTab={activeTab}
								onToggleExpand={() => toggleExpanded(row.macAddress)}
								onToggleSelect={() => handleDeviceSelection(row.macAddress)}
								onSetActiveTab={setActiveTab}
								onRefresh={() => onRefresh(row.macAddress)}
								expandedContent={
									<ShadowTabsPanel
										macAddress={row.macAddress}
										shadowClassic={row.shadowClassic}
										shadowRadar={row.shadowRadar}
										activeTab={activeTab}
										onSetActiveTab={setActiveTab}
									/>
								}
							/>
						))}
					</tbody>
				</table>
				<DeviceTableEmpty totalCount={data.length} filteredCount={filteredData.length} />
			</div>
		</div>
	)
}

export default DeviceTable
